import { MAX_CERTIFICATE_PAGE_BYTES } from '@felix/domain';
import { browserPdfOutput, type PayrollDocument, type PayrollSource } from '@felix/ui';
import { ApiError, api, officeApi } from './api.js';
import { tusUpload } from './tus-upload.js';

/**
 * Dekont yüklemesinin amacı: sunucu sınırı (25 MB) ve ödemeye bağlamada amaç denetimi buna göre
 * (`apps/server/src/office/uploads.ts` `PAYROLL_RECEIPT_UPLOAD_PURPOSE`; ADR 0066 §3).
 */
const PAYROLL_RECEIPT_UPLOAD_PURPOSE = 'payroll_receipt';
const id = encodeURIComponent;

/**
 * Ofiste Payroll (M16; ADR 0061 §5, ADR 0066): hesabı gemi yazar, ofis `payroll.view` yetkili
 * olduğu gemilerin hesabını salt okunur görür, pusula ve raporları dışa aktarır. Ödeme, dekont ve
 * mutabakatı ofis girer (`payroll.create`, `edit`, `void`). Ofisin dekontu ofis dosya ucundan,
 * gemiden gelmiş eski dekont ortak ek ucundan açılır. `owner`, yarım yüklemenin devamını kişiye
 * bağlar (inceleme M8a3-R22).
 */
export function officePayrollSource(owner?: string): PayrollSource {
  return {
    scope: 'web',
    load: (query) => officeApi.payroll(query),
    documents: (paymentId) =>
      api<PayrollDocument[]>(`/office/payroll/payments/${id(paymentId)}/documents`),
    documentUrl: (doc) =>
      doc.source === 'receipt'
        ? `/api/office/payroll/receipts/${id(doc.id)}/file`
        : `/api/office/attachments/${id(doc.id)}`,
    brand: async () => {
      try {
        const { branding } = await officeApi.branding();
        return branding?.name ? { name: branding.name, logo: branding.logo ?? null } : null;
      } catch {
        return null;
      }
    },
    pdf: browserPdfOutput(),
    // Şirketin extra türleri (ADR 0068 §2): ofiste payroll yetkilisi
    extraTypes: {
      save: ({ id: typeId, rowVersion, name }) =>
        typeId && rowVersion
          ? api<{ id: string }>(`/office/payroll/extra-types/${id(typeId)}`, {
              method: 'PATCH',
              body: { rowVersion, name },
            })
          : api<{ id: string }>('/office/payroll/extra-types', {
              method: 'POST',
              body: { name },
            }),
      remove: async (typeId, rowVersion, reason) => {
        await api(`/office/payroll/extra-types/${id(typeId)}/void`, {
          method: 'POST',
          body: { rowVersion, reason },
        });
      },
    },
    // Şirketin kesinti türleri (ADR 0080 §2): ofiste payroll yetkilisi; silinmez, pasiflenir
    deductionTypes: {
      save: ({ id: typeId, rowVersion, ...fields }) =>
        typeId && rowVersion
          ? api<{ id: string }>(`/office/payroll/deduction-types/${id(typeId)}`, {
              method: 'PATCH',
              body: { rowVersion, ...fields },
            })
          : api<{ id: string }>('/office/payroll/deduction-types', {
              method: 'POST',
              body: fields,
            }),
    },
    payments: {
      save: ({ vesselId, id: paymentId, rowVersion, periodId, input }) =>
        paymentId && rowVersion
          ? api<{ id: string }>(`/office/payroll/payments/${id(paymentId)}`, {
              method: 'PATCH',
              body: { rowVersion, input },
            })
          : api<{ id: string }>('/office/payroll/payments', {
              method: 'POST',
              body: { vesselId, periodId, input },
            }),
      remove: async (paymentId, rowVersion, reason) => {
        await api(`/office/payroll/payments/${id(paymentId)}/void`, {
          method: 'POST',
          body: { rowVersion, reason },
        });
      },
      reconcile: async (paymentId, rowVersion) => {
        await api(`/office/payroll/payments/${id(paymentId)}/reconcile`, {
          method: 'POST',
          body: { rowVersion },
        });
      },
      upload: async (paymentId, file) => {
        // Sunucu da denetler; büyük dosya hiç gönderilmez
        if (file.size <= 0 || file.size > MAX_CERTIFICATE_PAGE_BYTES) {
          throw new ApiError('FILE_TOO_LARGE', 413);
        }
        const uploadId = await tusUpload(file, {
          purpose: PAYROLL_RECEIPT_UPLOAD_PURPOSE,
          ...(owner ? { owner } : {}),
          onProgress: () => undefined,
        });
        await api(`/office/payroll/payments/${id(paymentId)}/receipts`, {
          method: 'POST',
          body: { uploadId, fileName: file.name },
        });
      },
    },
  };
}
